/**
 * Arena art asset loader (#172) — resolves an arena asset kind to a bundled
 * image URL, entirely client-side (presentation layer). The exact mirror of the
 * ability/aura icon loaders (`champions/abilityIcons.ts`, `auraIcons.ts`).
 *
 * BOUNDARY: arena art is cosmetic/presentation and MUST NOT touch the sim or any
 * hash. The sim's `MAP_SCHEMA` carries collision geometry only (`pillars[]` as
 * `{ x, y, radius }` circles); the *look* is resolved here from bundled PNGs,
 * never from hashed content. Deleting the PNGs returns the arena to its
 * procedural vector draw (the rip-out contract) — this loader just returns
 * `null` and the renderer falls back.
 *
 * ASSET RESOLUTION: PNGs live in `packages/content/assets/arena/<kind>.png`
 * (see that folder's README). Vite's `import.meta.glob` maps each file to a
 * content-hashed URL at build time. `arenaTextureSrc(kind)` returns that URL, or
 * `null` when no art is committed/loadable — the isometric floor, pillars, and
 * edge treatment are all ADDITIVE over the working procedural base.
 *
 * In non-Vite environments (vitest), `import.meta.glob` is undefined → empty map
 * → every lookup returns null → procedural fallback. Tests exercise the key /
 * fallback logic without real assets or a GPU.
 */

/**
 * The three arena art assets (bible §7). Authored for the tilted-overhead
 * camera:
 *   • `floor`  — a quiet tileable stone texture (opaque; tiles the
 *                ground plane beneath everything).
 *   • `pillar` — a column (base ellipse + visible height + rim light);
 *                anchored to its collision-circle footprint.
 *   • `edge`   — a low-wall / drop-off rim treatment replacing the flat border.
 */
export type ArenaAssetKind = "floor" | "pillar" | "edge";

/** All arena asset kinds, in a stable order (iteration / preload). */
export const ARENA_ASSET_KINDS: readonly ArenaAssetKind[] = ["floor", "pillar", "edge"];

/**
 * Build-time map of every arena asset PNG → its bundled URL. Eager + `?url` so
 * each entry is a ready string. Glob base is the content assets folder (outside
 * the client package; Vite `server.fs.allow` includes the repo root). Indexed by
 * the trailing `<kind>` key (the filename without extension).
 *
 * IMPORTANT: `import.meta.glob(...)` MUST be written as this literal call
 * expression. Vite transforms it only via static analysis of the literal form;
 * reading `.glob` off a cast/aliased `import.meta` hides the call from the
 * transformer, leaving it `undefined` at build → an empty map → assets never
 * bundle (the original #181 bug). Under vitest the literal form resolves real
 * committed URLs; absent files simply produce no entry (rip-out contract).
 */
function loadArenaUrlMap(): ReadonlyMap<string, string> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/assets/arena/**/*.png",
    { eager: true, query: "?url", import: "default" },
  );
  const map = new Map<string, string>();
  for (const [path, url] of Object.entries(modules)) {
    const key = assetKeyFromPath(path);
    if (key !== null) map.set(key, url);
  }
  return map;
}

/**
 * Extract the `<kind>` key (no extension) from a glob module path.
 * e.g. ".../assets/arena/floor.png" → "floor". Returns null if the path
 * doesn't sit under an `arena/` segment.
 */
function assetKeyFromPath(path: string): string | null {
  const marker = "/arena/";
  const idx = path.lastIndexOf(marker);
  if (idx === -1) return null;
  const tail = path.slice(idx + marker.length);
  return tail.replace(/\.png$/u, "");
}

const ARENA_URL_BY_KIND: ReadonlyMap<string, string> = loadArenaUrlMap();

/**
 * Resolve the bundled image URL for an arena asset `kind`, or `null` when no art
 * is committed/loadable. Pure lookup; the renderer falls back to its procedural
 * draw on null (never breaks — the rip-out contract).
 */
export function arenaTextureSrc(kind: ArenaAssetKind): string | null {
  return ARENA_URL_BY_KIND.get(kind) ?? null;
}
